import { FitViewOptions, useReactFlow, useStore, useViewport } from "reactflow";
import Icon from "app/components/shared/Icon";
import { ButtonHTMLAttributes, useCallback } from "react";

function ControlButton({
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      className="p-2 bg-white rounded-lg hover:bg-black hover:bg-purple-100 hover:text-purple-600 transition-colors duration-200 ease-in-out shadow-[inset_0_0_0_2px_#fff] disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-[white] text-charcoal"
    >
      {children}
    </button>
  );
}

interface Props {
  fitViewOptions: FitViewOptions;
}

export default function Controls({ fitViewOptions }: Props) {
  const { minZoomReached, maxZoomReached } = useStore((store) => ({
    minZoomReached: store.transform[2] <= store.minZoom,
    maxZoomReached: store.transform[2] >= store.maxZoom,
  }));

  const { zoomIn, zoomOut, fitView } = useReactFlow();
  const { zoom } = useViewport();

  const handleFitView = useCallback(() => fitView(fitViewOptions), [fitView]);
  const handleZoomIn = useCallback(() => zoomIn(), [zoomIn]);
  const handleZoomOut = useCallback(() => zoomOut(), [zoomOut]);

  return (
    <div className="absolute bottom-4 right-4 flex gap-2 z-10">
      <div className="rounded-lg bg-white shadow-depth-200">
        <ControlButton onClick={handleFitView} title="Center view">
          <Icon icon="location-crosshairs" className="h-4 w-5" />
        </ControlButton>
      </div>

      <div className="rounded-lg bg-white shadow-depth-200 flex items-baseline">
        <ControlButton
          onClick={handleZoomOut}
          title="Zoom out"
          disabled={minZoomReached}
        >
          <Icon icon="heroicons/outline/minus" className="h-4 w-5" />
        </ControlButton>
        <span className="inline-block center min-w-[2.5rem] text-xs">
          {Math.ceil(zoom * 100)}%
        </span>
        <ControlButton
          onClick={handleZoomIn}
          title="Zoom in"
          disabled={maxZoomReached}
        >
          <Icon icon="heroicons/outline/plus" className="h-4 w-5" />
        </ControlButton>
      </div>
    </div>
  );
}
